<template>
    <div>
        <step-form-heading v-if="!props.phoneNumberPoolId" :icon="faPhoneOffice">
            New Phone Pool

            <template v-slot:description> Create a new phone pool backed by a messaging service.</template>
        </step-form-heading>

        <team-phone-number-pool-details :phoneNumberPoolId="props.phoneNumberPoolId">
            <form-wrapper :sections="[]" :hideNav="true">
                <vx-alert
                    v-if="success && props.phoneNumberPoolId"
                    data-test="success-alert"
                    color="success"
                    :closeable="false"
                    shadow
                >
                    <template v-slot:icon>
                        <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                    </template>
                    <div class="flex items-center justify-between">
                        <span><strong>Existing Phone Pool</strong> updated successfully</span>
                        <vx-button :href="`/settings/teams/${teamId}#/phone-number-pools`" size="sm" color="success">
                            Back to Phone Pools
                            <font-awesome-icon widthAuto :icon="faSquareArrowUpLeft"></font-awesome-icon>
                        </vx-button>
                    </div>
                </vx-alert>

                <vx-expandable-card id="name">
                    <template v-slot:header> Name</template>

                    <template v-slot:content>
                        <vx-input
                            v-model="phoneNumberPool.friendly_name"
                            autofocus
                            :disabled="!isImpersonatingOrPlatformAdmin"
                            size="lg"
                            :error="displayFirstError(validate.friendly_name.$errors)"
                            :maxlength="props.phoneNumberPoolId ? 80 : 63"
                        ></vx-input>
                    </template>
                </vx-expandable-card>

                <vx-sticky-footer v-if="isImpersonatingOrPlatformAdmin">
                    <vx-button
                        :href="`/settings/teams/${teamId}#/phone-number-pools`"
                        class="w-20"
                        size="lg"
                        color="muted"
                    >
                        Cancel
                    </vx-button>
                    <vx-button
                        @click="submit()"
                        data-test="create"
                        class="grow"
                        size="lg"
                        color="primary"
                        :loading="saving"
                    >
                        {{ props.phoneNumberPoolId ? 'Update' : 'Create' }}
                    </vx-button>
                </vx-sticky-footer>
            </form-wrapper>
        </team-phone-number-pool-details>
    </div>
</template>

<script setup>
import { faCircleCheck, faPhoneOffice, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import TeamPhoneNumberPoolDetails from '~/components/settings/teams/phone-number-pools/TeamPhoneNumberPoolDetails.vue';
import FormWrapper from '~/components/general/FormWrapper.vue';
import { displayFirstError, validateRules } from '~/utils/validation';
import { VxAlert, VxButton, VxExpandableCard, VxInput, VxStickyFooter } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { usePhoneNumberPoolDetails } from '~/components/settings/teams/phone-number-pools/usePhoneNumberPoolDetails';
import { ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';

const props = defineProps({
    phoneNumberPoolId: {
        type: Number,
        required: false,
    },
});

const teamId = Spark.state.currentTeam.id;
const isImpersonatingOrPlatformAdmin = Spark.isImpersonating || Spark.isPlatformAdmin;
const { phoneNumberPool, save, saving, onMountedPhoneNumberPool } = usePhoneNumberPoolDetails();
const { $externalResults, catchError } = useFormRequestHandler();

const success = ref(false);

const validate = useVuelidate(
    {
        friendly_name: { required, maxLength: maxLength(props.phoneNumberPoolId ? 80 : 63) },
    },
    phoneNumberPool,
    { $externalResults }
);

onMountedPhoneNumberPool(() => {
    if (props.phoneNumberPoolId) {
        // reset validation to disable submit button after loading phone pool
        validate.value.$reset();
    }
});

const submit = async () => {
    const isValid = await validateRules(validate);
    success.value = false;

    if (!isValid) {
        return;
    }

    try {
        const response = await save();

        if (props.phoneNumberPoolId) {
            success.value = true;
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            window.location.assign(`/settings/teams/${teamId}/phone-number-pools/${response.id}/phone-numbers`);
        }
    } catch (e) {
        catchError(e);
    }
};
</script>
